import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { NativeSelect } from "@/components/ui/NativeSelect";
import type {
  AiCachedResponse,
  AiResolutionSuggestion,
  SupportTicket,
  TicketHistoryEntry,
  TicketHistoryResponse,
  TicketReply,
  TicketUrgency,
} from "@/lib/support";
import { replyAttachments, ticketLevelAttachments } from "@/lib/support";
import {
  callAiReplyDraft,
  callAiResolutionCheck,
  callAiTriage,
  fetchAiCached,
  fetchTicketHistory,
  fetchTicketMyNote,
  markSupportTicketRead,
  postTicketReply,
  saveTicketMetaChanges,
  saveTicketMyNote,
  updateTicketStatus,
  updateTicketUrgency,
  type TicketMetaDraft,
} from "@/lib/support";
import { SupportAttachments } from "@/pages/support/SupportAttachments";
import { TicketBrief } from "@/pages/support/TicketBrief";
import {
  selectCurrentSubject,
  statusNoteDuplicatesReply,
} from "@/pages/support/ticketConversation";
import {
  STATUS_LABEL,
  STATUS_OPTIONS,
  TODO_LABEL,
  URGENCY_LABEL,
  URGENCY_OPTIONS,
} from "@/pages/support/constants";
import { StatusBadge, UrgencyBadge } from "@/pages/support/SupportBadges";
import {
  ageInDays,
  datetimeLocalToIso,
  fmtDate,
  fmtRelative,
  isoToDatetimeLocal,
  normalizeTodoState,
  toastErr,
  toastOk,
} from "@/pages/support/utils";

function tagNamesKey(names: string[]): string {
  return [...names].map((n) => n.trim().toLowerCase()).filter(Boolean).sort().join("|");
}

function syncDraftFromTicket(
  merged: SupportTicket,
  setters: {
    setReplyStatus: (v: string) => void;
    setDraftTodoState: (v: string) => void;
    setDraftOwner: (v: string) => void;
    setDraftTags: (v: string[]) => void;
    setDraftDueAt: (v: string) => void;
    setDraftTodoNote: (v: string) => void;
  },
) {
  setters.setReplyStatus(String(merged.status || "open").toLowerCase());
  setters.setDraftTodoState(normalizeTodoState(merged.todo_state));
  setters.setDraftOwner(String(merged.todo_owner_login || "").trim().toLowerCase());
  setters.setDraftTags((merged.tags || []).map((t) => t.name).filter(Boolean));
  setters.setDraftDueAt(isoToDatetimeLocal(merged.todo_due_at));
  setters.setDraftTodoNote(String(merged.todo_note || ""));
}

function buildMetaDraft(
  status: string,
  todoState: string,
  owner: string,
  tags: string[],
  dueLocal: string,
  todoNote: string,
): TicketMetaDraft {
  return {
    status,
    statusNote: "",
    todo_state: todoState,
    todo_owner_login: owner,
    todo_due_at: datetimeLocalToIso(dueLocal),
    todo_note: todoNote,
    tagNames: tags,
  };
}

function mergeTicket(listTicket: SupportTicket, hist: TicketHistoryResponse): SupportTicket {
  const raw = hist.ticket || {};
  const tags = hist.tags ?? listTicket.tags;
  return {
    ...raw,
    ...listTicket,
    tags,
    message: raw.message ?? listTicket.message ?? null,
    created_at: raw.created_at ?? listTicket.created_at,
    updated_at: raw.updated_at ?? listTicket.updated_at,
  };
}

export function AdminDetailPanel({
  ticket,
  currentLogin,
  ownerOptions,
  onRefresh,
  onOpenHistory,
  onConfirmAiResolution,
  onTicketPatched,
  variant = "panel",
}: {
  ticket: SupportTicket;
  currentLogin: string;
  ownerOptions: { value: string; label: string }[];
  onRefresh: () => void;
  onOpenHistory: (t: SupportTicket) => void;
  onConfirmAiResolution: (ticketId: number, suggestion: AiResolutionSuggestion) => void;
  onTicketPatched?: (ticketId: number, patch: Partial<SupportTicket>) => void;
  variant?: "panel" | "modal";
}) {
  const [hist, setHist] = useState<TicketHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [replyStatus, setReplyStatus] = useState("open");
  const [ai, setAi] = useState<AiCachedResponse | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [draftTodoState, setDraftTodoState] = useState("eingang");
  const [draftOwner, setDraftOwner] = useState("");
  const [draftTags, setDraftTags] = useState<string[]>([]);
  const [draftDueAt, setDraftDueAt] = useState("");
  const [draftTodoNote, setDraftTodoNote] = useState("");
  const [draftMyNote, setDraftMyNote] = useState("");
  const [savedMyNote, setSavedMyNote] = useState("");
  const [myNoteStatus, setMyNoteStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [savingUrgency, setSavingUrgency] = useState(false);
  const ticketRef = useRef(ticket);
  ticketRef.current = ticket;
  const myNoteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    const listTicket = ticketRef.current;
    setLoading(true);
    try {
      const [data, noteRes] = await Promise.all([
        fetchTicketHistory(listTicket.id),
        fetchTicketMyNote(listTicket.id).catch(() => ({ note: String(listTicket.my_note || "") })),
      ]);
      setHist(data);
      const mergedOnLoad = mergeTicket(listTicket, data);
      syncDraftFromTicket(mergedOnLoad, {
        setReplyStatus,
        setDraftTodoState,
        setDraftOwner,
        setDraftTags,
        setDraftDueAt,
        setDraftTodoNote,
      });
      const note = String(noteRes.note || "");
      setDraftMyNote(note);
      setSavedMyNote(note);
      setMyNoteStatus("idle");
    } catch (err) {
      console.error(err);
      setHist(null);
    } finally {
      setLoading(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    void load();
    setReply("");
  }, [ticket.id, load]);

  const onTicketPatchedRef = useRef(onTicketPatched);
  onTicketPatchedRef.current = onTicketPatched;

  useEffect(() => {
    const id = ticket.id;
    const wasUnread = Boolean(ticketRef.current.is_unread);
    onTicketPatchedRef.current?.(id, { is_unread: false });
    let closed = false;
    void markSupportTicketRead(id).catch((err) => {
      console.error(err);
      // Schließen darf ein bereits gelesenes Ticket nicht zurücksetzen.
      if (!closed && wasUnread) {
        onTicketPatchedRef.current?.(id, { is_unread: true });
      }
    });
    return () => {
      closed = true;
      void markSupportTicketRead(id)
        .then(() => {
          onTicketPatchedRef.current?.(id, { is_unread: false });
        })
        .catch((err) => console.error(err));
    };
  }, [ticket.id]);

  useEffect(() => {
    return () => {
      if (myNoteTimerRef.current) clearTimeout(myNoteTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchAiCached(ticket.id).then((data) => {
      if (!cancelled) setAi(data);
      if (data?.ai_enabled && !data.items?.resolution_check) {
        const days = ageInDays(ticket.created_at);
        if (days >= 1) {
          void callAiResolutionCheck(ticket.id).then(() => fetchAiCached(ticket.id)).then(setAi);
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [ticket.id, ticket.created_at]);

  const merged = hist ? mergeTicket(ticket, hist) : ticket;
  const savedStatus = String(merged.status || "open").toLowerCase();
  const urgency = String(merged.urgency || "normal").toLowerCase();
  const todoState = normalizeTodoState(merged.todo_state);

  const savedSnapshot = useMemo(
    () =>
      buildMetaDraft(
        savedStatus,
        todoState,
        String(merged.todo_owner_login || "").trim().toLowerCase(),
        (merged.tags || []).map((t) => t.name).filter(Boolean),
        isoToDatetimeLocal(merged.todo_due_at),
        String(merged.todo_note || ""),
      ),
    [merged, savedStatus, todoState],
  );

  const internalDraftSnapshot = useMemo(
    () =>
      buildMetaDraft(
        savedStatus,
        draftTodoState,
        draftOwner,
        draftTags,
        draftDueAt,
        draftTodoNote,
      ),
    [savedStatus, draftTodoState, draftOwner, draftTags, draftDueAt, draftTodoNote],
  );

  const hasInternalChanges =
    internalDraftSnapshot.todo_state !== savedSnapshot.todo_state ||
    internalDraftSnapshot.todo_owner_login !== savedSnapshot.todo_owner_login ||
    internalDraftSnapshot.todo_due_at !== savedSnapshot.todo_due_at ||
    internalDraftSnapshot.todo_note !== savedSnapshot.todo_note ||
    tagNamesKey(draftTags) !== tagNamesKey(savedSnapshot.tagNames);

  const replyStatusChanged = replyStatus !== savedStatus;

  const timeline = useMemo(() => {
    const items: {
      at: string;
      kind: "status" | "reply" | "created";
      entry: TicketHistoryEntry | TicketReply | null;
      label?: string;
      body?: string;
    }[] = [];

    if (merged.created_at) {
      items.push({
        at: merged.created_at,
        kind: "created",
        entry: null,
        label: merged.reporter_display || merged.reporter_login || "Melder",
        body: merged.message || "",
      });
    }
    for (const h of hist?.history || []) {
      if (h.changed_at) items.push({ at: h.changed_at, kind: "status", entry: h });
    }
    for (const r of hist?.replies || []) {
      if (r.created_at) items.push({ at: r.created_at, kind: "reply", entry: r });
    }
    return items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [hist, merged]);

  const currentSubject = useMemo(
    () =>
      selectCurrentSubject({
        original: merged.message,
        reporterLogin: merged.reporter_login,
        history: hist?.history,
        replies: hist?.replies,
      }),
    [hist, merged.message, merged.reporter_login],
  );

  const ownerAcValue: AutocompleteOption<string> | null = draftOwner
    ? { key: draftOwner, label: draftOwner, value: draftOwner }
    : null;

  async function refreshAfterMutation() {
    await load();
    onRefresh();
  }

  async function persistMyNote(next: string) {
    setMyNoteStatus("saving");
    try {
      const res = await saveTicketMyNote(ticket.id, next);
      const note = String(res.note || "");
      setSavedMyNote(note);
      setDraftMyNote(note);
      setMyNoteStatus("saved");
      onTicketPatched?.(ticket.id, { my_note: note });
    } catch (err) {
      console.error(err);
      setMyNoteStatus("error");
      toastErr("Private Notiz konnte nicht gespeichert werden");
    }
  }

  function handleMyNoteChange(value: string) {
    setDraftMyNote(value);
    setMyNoteStatus("idle");
    if (myNoteTimerRef.current) clearTimeout(myNoteTimerRef.current);
    myNoteTimerRef.current = setTimeout(() => {
      if (value.trim() === savedMyNote.trim()) {
        setMyNoteStatus("idle");
        return;
      }
      void persistMyNote(value);
    }, 600);
  }

  async function handleUrgencyChange(next: TicketUrgency) {
    const prev = String(merged.urgency || "normal").toLowerCase();
    if (prev === next) return;
    setSavingUrgency(true);
    onTicketPatched?.(ticket.id, { urgency: next, is_unread: false });
    try {
      await updateTicketUrgency(ticket.id, next);
      toastOk(`Dringlichkeit: ${URGENCY_LABEL[next] || next}`);
      onRefresh();
    } catch (err) {
      console.error(err);
      onTicketPatched?.(ticket.id, { urgency: prev });
      toastErr("Dringlichkeit konnte nicht geändert werden");
    } finally {
      setSavingUrgency(false);
    }
  }

  async function handleSaveInternal() {
    if (!hasInternalChanges) return toastOk("Keine Änderungen zum Speichern");
    setSavingMeta(true);
    try {
      await saveTicketMetaChanges(ticket.id, savedSnapshot, internalDraftSnapshot);
      toastOk("Organisation gespeichert");
      await refreshAfterMutation();
    } catch (err) {
      console.error(err);
      toastErr("Änderungen konnten nicht gespeichert werden");
    } finally {
      setSavingMeta(false);
    }
  }

  async function handleSaveStatus() {
    if (!replyStatusChanged) return toastOk("Status ist unverändert");
    setSavingStatus(true);
    onTicketPatched?.(ticket.id, { status: replyStatus, is_unread: false });
    try {
      await updateTicketStatus(ticket.id, replyStatus, "");
      toastOk(`Status: ${STATUS_LABEL[replyStatus] || replyStatus}`);
      await refreshAfterMutation();
    } catch (err) {
      console.error(err);
      onTicketPatched?.(ticket.id, { status: savedStatus });
      toastErr("Status konnte nicht gespeichert werden");
    } finally {
      setSavingStatus(false);
    }
  }

  async function handleSendPublicReply() {
    const msg = reply.trim();
    if (!msg) return toastErr("Antwort darf nicht leer sein");
    setSending(true);
    try {
      if (replyStatusChanged) {
        await updateTicketStatus(ticket.id, replyStatus, "");
        onTicketPatched?.(ticket.id, { status: replyStatus, is_unread: false });
      }
      await postTicketReply(ticket.id, msg);
      setReply("");
      toastOk(
        replyStatusChanged
          ? "Antwort gesendet und Status aktualisiert"
          : "Antwort gesendet",
      );
      await refreshAfterMutation();
    } catch (err) {
      console.error(err);
      toastErr("Antwort konnte nicht gesendet werden");
    } finally {
      setSending(false);
    }
  }

  async function runAi(kind: "triage" | "reply_draft" | "resolution_check") {
    setAiLoading(true);
    try {
      let result;
      if (kind === "triage") result = await callAiTriage(ticket.id);
      else if (kind === "reply_draft") result = await callAiReplyDraft(ticket.id);
      else result = await callAiResolutionCheck(ticket.id);
      const cached = (await fetchAiCached(ticket.id)) || { items: {} };
      cached.items = { ...cached.items, [kind]: { ...result, payload: result.payload } };
      setAi(cached);
      if (kind === "resolution_check") onRefresh();
    } catch (err) {
      console.error(err);
      toastErr("KI-Aufruf fehlgeschlagen");
    } finally {
      setAiLoading(false);
    }
  }

  if (loading && !hist) {
    return <p className="py-12 text-center text-sm text-label-secondary">Ticket wird geladen …</p>;
  }

  const isModal = variant === "modal";

  return (
    <article
      className={clsx(
        "support-detail-card flex flex-col",
        isModal
          ? "overflow-visible bg-card"
          : "h-full min-h-0 overflow-hidden rounded-ios-lg border border-separator bg-card shadow-ios",
      )}
    >
      <header className="shrink-0 border-b border-separator px-4 py-3">
        {!isModal ? (
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
                Ticket #{merged.id}
              </p>
              <h2 className="mt-0.5 text-lg font-semibold text-label">{merged.title}</h2>
            </div>
            <Button type="button" variant="secondary" className="text-xs" onClick={() => onOpenHistory(merged)}>
              Historie
            </Button>
          </div>
        ) : null}
        <div className={clsx("flex flex-wrap items-center gap-2", !isModal && "mt-3")}>
          <span className="text-xs font-semibold uppercase text-label-tertiary">Aktueller Status</span>
          <StatusBadge status={savedStatus} label={STATUS_LABEL[savedStatus] || savedStatus} />
          <UrgencyBadge urgency={urgency} label={URGENCY_LABEL[urgency] || urgency} />
          <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-semibold text-label">
            {TODO_LABEL[todoState] || todoState}
          </span>
          <NativeSelect
            selectSize="sm"
            className="text-xs py-1"
            value={urgency}
            disabled={savingUrgency}
            aria-label="Dringlichkeit"
            onChange={(e) => void handleUrgencyChange(e.target.value as TicketUrgency)}
          >
            {URGENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </NativeSelect>
          {isModal ? (
            <Button type="button" variant="secondary" className="ml-auto text-xs" onClick={() => onOpenHistory(merged)}>
              Historie
            </Button>
          ) : null}
        </div>
        <p className="mt-2 text-xs text-label-secondary">
          {merged.program || "—"} · {merged.reporter_display || merged.reporter_login || "—"} · erstellt{" "}
          {fmtRelative(merged.created_at)}
        </p>
      </header>

      <div
        className={clsx(
          "space-y-5 px-4 py-4",
          !isModal && "min-h-0 flex-1 overflow-y-auto",
        )}
      >
        <TicketBrief
          original={merged.message || ""}
          originalAt={merged.created_at}
          reporterLabel={merged.reporter_display || merged.reporter_login || "Melder"}
          current={currentSubject}
          attachments={
            ticketLevelAttachments(hist?.attachments).length ? (
              <SupportAttachments
                attachments={ticketLevelAttachments(hist?.attachments)}
                variant="grid"
                title="Anhänge zum Anliegen"
              />
            ) : null
          }
        />

        {/* 2. Antwort + Status (geht an den Melder) */}
        <section className="rounded-ios border border-accent/25 bg-accent-soft/10 p-4">
          <h3 className="text-sm font-semibold text-label">Status und Antwort</h3>
          <p className="mt-1 text-xs text-label-secondary">
            Status kannst du allein speichern. Eine Antwort ist optional und wird nur mit „Antwort senden“ verschickt.
          </p>

          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase text-label-tertiary">Neuer Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((opt) => {
                const isCurrent = opt.value === savedStatus;
                const isSelected = opt.value === replyStatus;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={clsx(
                      "rounded-full px-3 py-1.5 text-sm font-medium transition",
                      isSelected
                        ? "bg-accent text-label-on-accent shadow-sm"
                        : "bg-card text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary",
                      isCurrent && !isSelected && "ring-accent/40",
                    )}
                    onClick={() => setReplyStatus(opt.value)}
                  >
                    {opt.label}
                    {isCurrent ? " · aktuell" : ""}
                  </button>
                );
              })}
            </div>
            {replyStatusChanged ? (
              <p className="mt-2 text-xs text-accent">
                Statusänderung: {STATUS_LABEL[savedStatus] || savedStatus} →{" "}
                {STATUS_LABEL[replyStatus] || replyStatus}
              </p>
            ) : (
              <p className="mt-2 text-xs text-label-tertiary">Status bleibt unverändert ({STATUS_LABEL[savedStatus] || savedStatus}).</p>
            )}
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="secondary"
                disabled={!replyStatusChanged || savingStatus || sending}
                onClick={() => void handleSaveStatus()}
              >
                {savingStatus ? "Speichert …" : "Speichern"}
              </Button>
            </div>
          </div>

          <label className="mt-4 block">
            <span className="mb-1 block text-xs font-semibold uppercase text-label-tertiary">Antwort (optional)</span>
            <textarea
              className="eh-input min-h-[5rem] text-sm"
              placeholder="Antwort an den Melder …"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
          </label>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-label-tertiary">
              Die Antwort geht per E-Mail an den Melder und erscheint im Verlauf. Ein geänderter Status wird mitgespeichert.
            </span>
            <Button
              type="button"
              disabled={sending || savingStatus || savingMeta || !reply.trim()}
              onClick={() => void handleSendPublicReply()}
            >
              {sending ? "Wird gesendet …" : "Antwort senden"}
            </Button>
          </div>

          <AiPanel
            ai={ai}
            loading={aiLoading}
            ticket={merged}
            onRun={runAi}
            onApplyReply={(draft) => setReply(draft)}
            onConfirmResolution={(sugg) => onConfirmAiResolution(ticket.id, sugg)}
            onApplyTriage={(payload) => {
              const p = payload as Record<string, unknown>;
              setDraftTodoState(String(p.todo_state || "eingang"));
              const tags = Array.isArray(p.tags) ? (p.tags as string[]) : [];
              if (tags.length) {
                setDraftTags((prev) => [...new Set([...prev, ...tags])]);
              }
              toastOk("Triage in interne Notizen übernommen");
            }}
          />
        </section>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-label">Verlauf</h3>
              <p className="mt-0.5 text-xs text-label-tertiary">
                Nachrichten einmal, Statuswechsel nur als Zeile.
              </p>
            </div>
            <StatusBadge status={savedStatus} label={`Jetzt: ${STATUS_LABEL[savedStatus] || savedStatus}`} />
          </div>
          <div className="mt-3 space-y-2">
            {!timeline.length ? (
              <p className="text-sm text-label-tertiary">Noch keine Aktivität protokolliert.</p>
            ) : (
              timeline.map((item, i) => {
                if (item.kind === "created") {
                  return (
                    <ThreadStatus
                      key={`created-${i}`}
                      at={item.at}
                      actor={item.label || "Melder"}
                      text="Anliegen eingegangen"
                    />
                  );
                }
                if (item.kind === "status") {
                  const h = item.entry as TicketHistoryEntry;
                  const newSt = String(h.new_status || "").toLowerCase();
                  const hideNote = statusNoteDuplicatesReply(h.note, h.changed_at, hist?.replies);
                  return (
                    <ThreadStatus
                      key={`status-${i}`}
                      at={h.changed_at || ""}
                      actor={h.changed_by_display || h.changed_by_login || "Support"}
                      text={`${STATUS_LABEL[h.old_status || ""] || h.old_status || "—"} →`}
                      badge={<StatusBadge status={newSt} label={STATUS_LABEL[newSt] || newSt || "?"} />}
                      note={hideNote ? null : h.note}
                    />
                  );
                }
                const r = item.entry as TicketReply;
                const fromReporter =
                  String(r.author_login || "").trim().toLowerCase() ===
                  String(merged.reporter_login || "").trim().toLowerCase();
                return (
                  <ThreadMessage
                    key={`reply-${r.id ?? i}`}
                    at={r.created_at || ""}
                    actor={r.author_display || r.author_login || "Support"}
                    title={fromReporter ? "Rückmeldung" : "Antwort"}
                    tone={fromReporter ? "reporter" : "support"}
                  >
                    {r.message?.trim() ? r.message : null}
                    {replyAttachments(hist?.attachments, r.id).length ? (
                      <SupportAttachments
                        attachments={replyAttachments(hist?.attachments, r.id)}
                        variant="grid"
                        showTitle={false}
                        className="mt-2"
                      />
                    ) : null}
                  </ThreadMessage>
                );
              })
            )}
          </div>
        </section>

        {/* 4. Private Notiz */}
        <section className="rounded-ios border border-accent/20 bg-accent-soft/10 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-label">Meine Notiz</h3>
            <span className="text-[11px] text-label-tertiary">
              {myNoteStatus === "saving"
                ? "Speichert …"
                : myNoteStatus === "saved"
                  ? "Gespeichert"
                  : myNoteStatus === "error"
                    ? "Fehler beim Speichern"
                    : "Nur für dich sichtbar"}
            </span>
          </div>
          <p className="mt-1 text-xs text-label-secondary">
            Private Notiz zu diesem Ticket – andere Admins sehen sie nicht.
          </p>
          <textarea
            className="eh-input mt-3 min-h-[5rem] text-sm"
            rows={4}
            maxLength={4000}
            placeholder="Deine privaten Notizen …"
            value={draftMyNote}
            onChange={(e) => handleMyNoteChange(e.target.value)}
          />
        </section>

        {/* 5. Organisation (To-Do, Tags, Bearbeiter) */}
        <section className="rounded-ios border border-dashed border-separator bg-fill-quaternary/10 p-4">
          <h3 className="text-sm font-semibold text-label">Organisation</h3>
          <p className="mt-1 text-xs text-label-secondary">
            Spalte, Tags und Bearbeiter – für das gesamte Support-Team sichtbar.
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Control label="Spalte (To-Do)">
              <NativeSelect value={draftTodoState} onChange={(e) => setDraftTodoState(e.target.value)}>
                {Object.entries(TODO_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </NativeSelect>
            </Control>
            <Control label="Fälligkeit">
              <input
                type="datetime-local"
                className="eh-input text-sm"
                value={draftDueAt}
                onChange={(e) => setDraftDueAt(e.target.value)}
              />
            </Control>
            <Control label="Bearbeiter" className="sm:col-span-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[12rem] flex-1">
                  <LockedAutocomplete<string>
                    placeholder="(nicht zugewiesen)"
                    value={ownerAcValue}
                    minChars={0}
                    allowFreeText
                    loadOptions={async (q) => {
                      const term = q.trim().toLowerCase();
                      return ownerOptions
                        .filter((o) => !term || o.label.includes(term))
                        .map((o) => ({ key: o.value, label: o.label, value: o.value }));
                    }}
                    onChange={(opt) => setDraftOwner(opt?.value?.trim().toLowerCase() ?? "")}
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="text-xs"
                  onClick={() => {
                    setDraftOwner(currentLogin);
                    setDraftTodoState("in_umsetzung");
                  }}
                >
                  Ich
                </Button>
                {draftOwner ? (
                  <Button type="button" variant="secondary" className="text-xs" onClick={() => setDraftOwner("")}>
                    ×
                  </Button>
                ) : null}
              </div>
            </Control>
            <Control label="Themen / Tags" className="sm:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                {draftTags.length ? (
                  draftTags.map((name) => (
                    <span
                      key={name}
                      className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label"
                    >
                      {name}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-label-tertiary">keine</span>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  className="text-xs"
                  onClick={() => {
                    const raw = window.prompt("Tags (kommagetrennt):", draftTags.join(", "));
                    if (raw == null) return;
                    setDraftTags(
                      raw
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean),
                    );
                  }}
                >
                  + bearbeiten
                </Button>
              </div>
            </Control>
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="secondary"
              disabled={!hasInternalChanges || savingMeta || sending}
              onClick={() => void handleSaveInternal()}
            >
              {savingMeta ? "Speichert …" : "Organisation speichern"}
            </Button>
          </div>
        </section>
      </div>
    </article>
  );
}

function personInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((part) => part[0]?.toUpperCase() || "").join("");
  return letters || "?";
}

function ThreadMessage({
  at,
  actor,
  title,
  tone,
  children,
}: {
  at: string;
  actor: string;
  title: string;
  tone: "reporter" | "support";
  children: React.ReactNode;
}) {
  return (
    <article
      className={clsx(
        "rounded-ios border px-3.5 py-3",
        tone === "reporter" ? "border-accent/30 bg-accent/[0.06]" : "border-separator bg-fill-quaternary/15",
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={clsx(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
            tone === "reporter" ? "bg-accent text-label-on-accent" : "bg-fill-tertiary text-label",
          )}
          aria-hidden
        >
          {personInitials(actor)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-sm font-medium text-label">
              {actor}
              <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
                {title}
              </span>
            </p>
            <span className="text-xs text-label-tertiary" title={fmtDate(at)}>
              {fmtRelative(at)}
            </span>
          </div>
          <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-label">{children}</div>
        </div>
      </div>
    </article>
  );
}

function ThreadStatus({
  at,
  actor,
  text,
  badge,
  note,
}: {
  at: string;
  actor: string;
  text: string;
  badge?: React.ReactNode;
  note?: string | null;
}) {
  return (
    <div className="px-1 py-1">
      <div className="flex items-center gap-3 text-xs text-label-secondary">
        <span className="h-px min-w-4 flex-1 bg-separator/80" aria-hidden />
        <span className="flex max-w-[85%] flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center">
          <span className="font-medium text-label">{actor}</span>
          <span aria-hidden>·</span>
          <span>{text}</span>
          {badge}
          {at ? (
            <>
              <span aria-hidden>·</span>
              <span title={fmtDate(at)}>{fmtRelative(at)}</span>
            </>
          ) : null}
        </span>
        <span className="h-px min-w-4 flex-1 bg-separator/80" aria-hidden />
      </div>
      {note ? (
        <p className="mx-auto mt-2 max-w-3xl whitespace-pre-wrap rounded-ios border border-separator/70 bg-fill-quaternary/15 px-3 py-2 text-sm leading-relaxed text-label">
          {note}
        </p>
      ) : null}
    </div>
  );
}

function Control({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-semibold uppercase text-label-tertiary">{label}</label>
      {children}
    </div>
  );
}

function AiPanel({
  ai,
  loading,
  ticket,
  onRun,
  onApplyReply,
  onConfirmResolution,
  onApplyTriage,
}: {
  ai: AiCachedResponse | null;
  loading: boolean;
  ticket: SupportTicket;
  onRun: (k: "triage" | "reply_draft" | "resolution_check") => void;
  onApplyReply: (draft: string) => void;
  onConfirmResolution: (s: AiResolutionSuggestion) => void;
  onApplyTriage: (payload: Record<string, unknown>) => void;
}) {
  const items = ai?.items || {};
  if (!ai?.ai_enabled) {
    return (
      <div className="mt-4 rounded-ios border border-dashed border-separator/80 p-3 text-xs text-label-tertiary">
        KI-Funktionen sind nicht aktiv (kein OpenAI-Key).
      </div>
    );
  }

  const resolution = items.resolution_check;
  const triage = items.triage;
  const replyDraft = items.reply_draft;

  return (
    <div className="mt-4 rounded-ios border border-separator/60 bg-card/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-label">KI-Assistent</span>
        <div className="flex flex-wrap gap-1">
          <Button type="button" variant="secondary" className="text-xs" disabled={loading} onClick={() => onRun("triage")}>
            Triage
          </Button>
          <Button type="button" variant="secondary" className="text-xs" disabled={loading} onClick={() => onRun("reply_draft")}>
            Antwort
          </Button>
          <Button type="button" variant="secondary" className="text-xs" disabled={loading} onClick={() => onRun("resolution_check")}>
            Erledigt-Check
          </Button>
        </div>
      </div>
      {loading ? (
        <p className="mt-2 text-xs text-label-secondary">KI analysiert …</p>
      ) : (
        <div className="mt-3 space-y-2 text-xs">
          {resolution?.payload && (resolution.payload as { likely_resolved?: boolean }).likely_resolved ? (
            <div className="rounded-ios border border-status-green/40 bg-status-green/10 p-2">
              <p className="font-medium text-status-green">Erledigt-Vorschlag</p>
              <p>{String((resolution.payload as { reason?: string }).reason || "")}</p>
              <Button
                type="button"
                className="mt-2 text-xs"
                onClick={() =>
                  onConfirmResolution({
                    ticket_id: ticket.id,
                    reason: String((resolution.payload as { reason?: string }).reason),
                    confirm_message: String((resolution.payload as { confirm_message?: string }).confirm_message),
                    suggested_status: String((resolution.payload as { suggested_status?: string }).suggested_status),
                    suggested_todo_state: String((resolution.payload as { suggested_todo_state?: string }).suggested_todo_state),
                  })
                }
              >
                Als gelöst bestätigen
              </Button>
            </div>
          ) : null}
          {triage?.payload ? (
            <div className="rounded-ios border border-separator/60 p-2">
              <p className="font-medium">Triage</p>
              <p className="text-label-secondary">{String((triage.payload as { summary?: string }).summary || "")}</p>
              <Button
                type="button"
                variant="secondary"
                className="mt-2 text-xs"
                onClick={() => onApplyTriage(triage.payload as Record<string, unknown>)}
              >
                In interne Notizen übernehmen
              </Button>
            </div>
          ) : null}
          {replyDraft?.payload && (replyDraft.payload as { draft?: string }).draft ? (
            <div className="rounded-ios border border-separator/60 p-2">
              <p className="font-medium">Antwort-Entwurf</p>
              <p className="whitespace-pre-wrap text-label-secondary">
                {String((replyDraft.payload as { draft?: string }).draft)}
              </p>
              <Button
                type="button"
                variant="secondary"
                className="mt-2 text-xs"
                onClick={() => onApplyReply(String((replyDraft.payload as { draft?: string }).draft))}
              >
                In Antwort übernehmen
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
